import { PersonalDocument } from "../../domain";
import { option, taskEither, either } from "fp-ts";
import { useFormatMessage } from "../../../intl";
import { PersonalDocumentForm } from "./PersonalDocumentForm";
import {
  useForm,
  Box,
  Button,
  Space,
  Validator,
  validators,
  LocalizedString,
  useIsMobileLayout,
} from "@dbn/omnichannel-design-system-g4p";
import { FormState } from "../PersonalDocumentForm/PersonalDocumentForm";
import { Option } from "fp-ts/Option";
import { DocumentType } from "../../../UploadDocuments/domain";
import { useValidators } from "../../../Common/useValidators";
import { flow, pipe } from "fp-ts/function";
import { CloseIcon } from "@g4p/react-styleguide-library";
import "./styles.css";

type Props = {
  documentType: DocumentType;
  initialValues: FormState;
  onValidate: Validator<PersonalDocument>;
  onSave: (personalDocument: PersonalDocument) => unknown;
  onCancel: () => unknown;
  authorityMandatory: boolean;
  issuerMandatory: boolean;
  validUntilMandatory: boolean;
};

export function PersonalDocumentFormEdit(props: Props) {
  const formatMessage = useFormatMessage();

  const { nonBlankString, defined, validDate } = useValidators();

  const { fieldProps, handleSubmit, formErrors } = useForm(
    {
      initialValues: props.initialValues,
      fieldValidators: ({ validUntil }) => ({
        idNumber: nonBlankString,
        authority: props.authorityMandatory
          ? validators.inSequence(
              validators.definedNoExtract(
                formatMessage("Form.fieldError.required")
              ),
              validators.validateIfDefined(nonBlankString)
            )
          : undefined,
        issuer: props.issuerMandatory
          ? validators.inSequence(
              validators.definedNoExtract(
                formatMessage("Form.fieldError.required")
              ),
              validators.validateIfDefined(nonBlankString)
            )
          : undefined,
        dateOfIssuing: validators.inSequence(
          defined<Option<Date>>(),
          validDate,
          validators.fromPredicate<Date>(
            p => p <= new Date(),
            formatMessage("Form.fieldError.issuingAfterToday")
          ),
          validators.fromPredicate<Date>(p => {
            return option.isSome(validUntil) && option.isSome(validUntil.value)
              ? validUntil.value.value > p
              : true;
          }, formatMessage("Form.fieldError.validUntilBeforeIssuing"))
        ),
        validUntil: pipe(
          props.initialValues.validUntil,
          option.fold(
            () =>
              option.isSome(validUntil) &&
              option.isSome(validUntil.value) &&
              props.validUntilMandatory
                ? validators.inSequence(defined(), validDate, validDate =>
                    taskEither.of(option.some(validDate))
                  )
                : validators.validator(
                    option.fold(
                      () =>
                        either.right<LocalizedString, Option<Date>>(
                          option.none
                        ),
                      () =>
                        either.left(formatMessage("Form.fieldError.notBlank"))
                    )
                  ),
            () =>
              validators.inSequence(
                defined(),
                validDate,
                validators.fromPredicate<Date>(
                  p => p > new Date(),
                  formatMessage("Form.fieldError.validDateExpired")
                ),
                validDate => taskEither.of(option.some(validDate))
              )
          )
        ),
      }),
    },
    {
      onSubmit: flow(
        props.onValidate,
        taskEither.chain(values =>
          taskEither.fromIO(() => props.onSave(values))
        )
      ),
    }
  );

  const WrappedIcon = ({ size }: { size: "default" }) => {
    const iconSize = size === "default" ? 14 : 14;
    return <CloseIcon width={iconSize} height={iconSize} />;
  };

  const cancelButton = (
    <Button
      variant="text"
      size="default"
      icon={WrappedIcon}
      action={props.onCancel}
      label={formatMessage("Cancel")}
    />
  );

  const saveButton = (
    <Button
      variant="primary"
      size="default"
      action={handleSubmit}
      label={formatMessage("Save")}
    />
  );

  const isMobileLayout = useIsMobileLayout();

  return (
    <Box column>
      <PersonalDocumentForm
        documentType={props.documentType}
        fieldProps={fieldProps}
        readOnly={false}
        errors={formErrors}
        canEditValidUntilField={
          option.isSome(props.initialValues.validUntil) ||
          props.validUntilMandatory
        }
        issue={option.none}
        reworkDocumentDetails={option.none}
        reworkClientProfile={option.none}
        authorityMandatory={props.authorityMandatory}
        issuerMandatory={props.issuerMandatory}
      />
      <Space units={5} />

      <Box
        column={isMobileLayout}
        grow
        shrink
        width={isMobileLayout ? "100%" : undefined}
        vAlignContent="center"
        hAlignContent={isMobileLayout ? "center" : "right"}
      >
        {isMobileLayout ? saveButton : cancelButton}
        <Space units={4} />
        {isMobileLayout ? cancelButton : saveButton}
      </Box>
    </Box>
  );
}
